import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FORMS_COPY, JOURNAL_TEMPLATES, type JournalTemplate } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card } from '@/components/ui';
import { apiBlob } from '@/lib/api';

/**
 * 양식 자료실 — 보육일지 다섯 양식 (§5.14, v1.19)
 * PDF로 받아 종이로 쓰거나, 같은 양식으로 보육일지 화면에서 바로 작성한다.
 */
export function FormsPage() {
  const navigate = useNavigate();
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = async (template: JournalTemplate) => {
    setBusyCode(template.code);
    setError(null);
    try {
      const blob = await apiBlob(`/form-templates/${template.code}/pdf`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${template.name}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('PDF를 받지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <Screen>
      <AppBar title={FORMS_COPY.title} back />

      <p className="px-4 pt-3 text-sm text-ink-sub">{FORMS_COPY.intro}</p>

      {error && (
        <p role="alert" className="mx-4 mt-3 rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      <ul className="flex flex-col gap-3 p-4">
        {JOURNAL_TEMPLATES.map((template) => (
          <Card key={template.code} as="li">
            <p className="font-bold">{template.name}</p>
            <p className="mt-0.5 text-base">{template.summary}</p>
            <p className="mt-1 text-sm text-ink-sub">{template.origin}</p>
            <div className="mt-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                loading={busyCode === template.code}
                onClick={() => void download(template)}
              >
                {FORMS_COPY.download}
              </Button>
              <Button size="sm" onClick={() => navigate(`/care-journal?template=${template.code}`)}>
                {FORMS_COPY.writeInApp}
              </Button>
            </div>
          </Card>
        ))}
      </ul>
    </Screen>
  );
}
